/* eslint-disable consistent-return */
import { BlobServiceClient, BlockBlobParallelUploadOptions } from '@azure/storage-blob';
import { AzureSASCredential, TableClient, TableEntity } from '@azure/data-tables';
import { v4 as uuidv4 } from 'uuid';
import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile } from '@ffmpeg/util';
import axios from 'axios';
import { FileType3D, FileTypeImage, FileTypeVideo, StorageLocation } from '@/types/enum/upload';
import { OBJExporter } from 'three/examples/jsm/exporters/OBJExporter.js';
import * as THREE from 'three';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader.js';
import { Mesh, MeshBasicMaterial } from 'three';

// Replace with your shared access token.
// Get access to your azure blob & tables storage account.
export const blobUrlGeneral = import.meta.env.VITE_AZURE_BLOB_URL;
const blobUrlFormBuilder = import.meta.env.VITE_AZURE_BLOB_URL_FORMBUILDER;
export const sasTokenGeneral = import.meta.env.VITE_AZURE_SAS_TOKEN;
const sasTokenFormBuilder = import.meta.env.VITE_AZURE_SAS_TOKEN_FORMBUILDER;
const tablesUrl = import.meta.env.VITE_AZURE_TABLES_URL;
const tablesSasToken = import.meta.env.VITE_AZURE_TABLES_SAS_TOKEN;

// Define & export interface ...
export interface ProductVideoFiles {
  uid?: number;
  video: string;
  videoFiletype: FileTypeVideo;
  videoUrl: string;
  videoThumbnail: string;
  videoThumbnailFiletype: FileTypeImage;
  videoThumbnailUrl: string;
}

export interface TableField {
  productId: string;
  filename: string;
  fileType: FileTypeImage | FileTypeVideo;
  fileUrl: string;
}

export interface ExtendedTableEntity extends TableEntity {
  fileType: FileTypeImage | FileTypeVideo;
  fileUrl: string;
}

export interface VideoDetails {
  video: string | null;
  videoFiletype: FileTypeVideo | null;
  videoUrl: string | null;
  videoThumbnail: string | null;
  videoThumbnailFiletype: FileTypeImage | null;
  videoThumbnailUrl: string | null;
}

export interface Product3DFile {
  filename: string;
  fileType: FileType3D;
  fileContent: string | ArrayBuffer;
  storageLocation: StorageLocation;
}

export interface ExtractedMesh {
  name: string;
  base64Content: string;
}

export enum Convert3DFileType {
  MESH = 'three',
  STRING = 'image',
}

const alertConnectionOffline = 'Connection is lost during the upload process. The upload will resume once the connection is back up.';
const alertConnectionOnline = 'Connection is active again. Continue the upload process.';

export const fileContentToBase64 = (file: File, callback: (encodeString: string) => void): void => {
  const reader = new FileReader();
  reader.readAsDataURL(file);
  reader.onload = () => {
    callback(reader.result as string);
  };
};

export function extractExtensionFromBase64(base64String: string): string {
  const match = base64String.match(/^data:(image\/[a-zA-Z]+);base64,/);
  if (match) {
    const mimeType = match[1];
    const extension = mimeType.split('/')[1]; // Get the extension part from MIME type
    return extension;
  }
  return 'png'; // Default to 'png' if MIME type cannot be determined
}

export function generateRandomFilename(extension = 'png') {
  // Create a random part using Date.now() and Math.random()
  const randomPart = Date.now() + '-' + Math.round(Math.random() * 1e9);

  // Combine the random part with the desired file extension
  return `file-${randomPart}.${extension}`;
}

// Convert blob URL to file
export async function blobUrlToFile(blobUrl: string, fileName: string): Promise<File> {
  // Fetch the blob data from the blob URL
  const response = await fetch(blobUrl);
  const blob = await response.blob();

  // Create a new file from the blob with the specified file name and type
  const file = new File([blob], fileName, { type: blob.type });

  return file;
}

// Image default
export const defaultImage = '/assets/no-image.jpg';

// Generate filename like slug with unique uuid...
export function generateFilename(fileName: string): string {
  // Pisahkan nama file dan ekstensi
  const fileExtension = fileName.split('.').pop();
  const nameWithoutExtension = fileName.replace(/\.[^/.]+$/, '');

  // Generate slug dari nama file tanpa ekstensi
  let filename = nameWithoutExtension
    .toLowerCase() // Ubah ke huruf kecil
    .trim() // Hapus spasi di awal dan akhir
    .replace(/[^\w\s-]/g, '') // Hapus karakter non-alphanumeric kecuali spasi dan tanda hubung
    .replace(/\s+/g, '-') // Ganti spasi dengan tanda hubung
    .replace(/-+/g, '-'); // Ganti tanda hubung berturut-turut menjadi satu tanda hubung

  return (filename = `${filename}-${uuidv4()}.${fileExtension}`);
}

// Generate thumbnail based on the original file extension
export async function generateVideoThumbnail(file: File, prefix = 'thumbnail'): Promise<File> {
  const ffmpeg = new FFmpeg();

  if (!ffmpeg.loaded) {
    await ffmpeg.load();
  }

  // Get the original file extension
  const fileExtension = file.name.split('.').pop(); // Extract file extension
  const fileName = file.name.replace(/\.[^/.]+$/, '');
  const inputFileName = `input.${fileExtension}`; // Use the original extension for the input file
  const outputFileName = `${prefix}-${fileName}.png`; // Use the original extension for the input file

  // Write the original file into FFmpeg's filesystem
  await ffmpeg.writeFile(inputFileName, await fetchFile(file));

  // Extract frame at 1 second
  await ffmpeg.exec(['-i', inputFileName, '-ss', '00:00:01', '-frames:v', '1', outputFileName]);

  // Read the extracted thumbnail
  const result = await ffmpeg.readFile(outputFileName);

  // Convert data to base64 image URL
  const blobUrl = URL.createObjectURL(new Blob([result as unknown as ArrayBuffer], { type: 'image/png' }));

  // Convert to file image
  const imageFile = await blobUrlToFile(blobUrl, outputFileName);

  return imageFile;
}

// Function upload file to Azure Blob Storage
export async function uploadToAzure({
  file,
  direcatoryPath,
  containerName = 'uploadfile',
  type = 'general',
}: {
  file: File;
  direcatoryPath: string;
  containerName?: string;
  type?: string;
}): Promise<string> {
  const sasToken = type === 'general' ? sasTokenGeneral : sasTokenFormBuilder;
  const blobUrl = type === 'general' ? blobUrlGeneral : blobUrlFormBuilder;

  if (!blobUrl || !sasToken) {
    throw new Error('Blob URL or SAS Token is missing');
  }

  // Modify filename metadata
  const fileName = `${direcatoryPath}/${generateFilename(file.name)}`;
  const newFile = new File([file], fileName, { type: file.type });

  const blobServiceClient = new BlobServiceClient(`${blobUrl}${sasToken}`);
  const containerClient = blobServiceClient.getContainerClient(containerName);
  const blockBlobClient = containerClient.getBlockBlobClient(newFile.name);

  const uploadOptions: BlockBlobParallelUploadOptions = {
    blobHTTPHeaders: {
      blobContentType: file.type,
    },
  };

  await blockBlobClient.uploadData(file, uploadOptions);
  return blockBlobClient.url;
}

/**
 * Function to delete files from Azure Blob Storage using URL and SAS Token
 * @param fileName 👉 Name of the file to be deleted
 */
export async function deleteFileFromAzure(fileURL: string): Promise<boolean> {
  try {
    // Delete a file by performing an HTTP DELETE request
    await axios.delete(fileURL);
    return true;
  } catch (error) {
    throw new Error(`Failed to delete files from Azure Blob Storage: ${error}`);
  }
}

// Function to call download
export async function downloadFileFromAzure(fileName: string, fileUrl: string): Promise<void> {
  try {
    // Fetch file data
    const response = await fetch(fileUrl);
    const blob = await response.blob();

    // Create a URL for the blob
    const url = window.URL.createObjectURL(blob);

    // Create an <a> element to trigger the download
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName; // Name of the desired file
    document.body.appendChild(link);
    link.click();

    // Remove the <a> element after the download is complete
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url); // Clear URL blob from memory
  } catch (error) {
    throw new Error(`Error downloading file: ${error}`);
  }
}

async function authAzureTableStorage(): Promise<TableClient> {
  // authenticate Table Client with SAS token
  if (!tablesUrl || !tablesSasToken) {
    throw new Error('Tables URL or SAS Token is missing');
  }

  const tableName = 'uploadFile';
  const tableClient = new TableClient(tablesUrl, tableName, new AzureSASCredential(tablesSasToken));

  return tableClient;
}

// Function to upload file metadata to Azure Table
export async function saveFileMetadataToAzureTables(payloads: TableField[]): Promise<void> {
  if (!payloads) return;

  // authenticate Table Client with SAS token
  const tableClient = await authAzureTableStorage();

  if (!tableClient) {
    throw new Error(`tableClient not found`);
  }

  try {
    // Define the entity to insert (ensure PartitionKey and RowKey are correctly named)
    await Promise.all(
      payloads.map(async (payload) => {
        const entity: ExtendedTableEntity = {
          partitionKey: payload.productId, // Properti case-sensitive
          rowKey: payload.filename, // Properti case-sensitive
          fileType: payload.fileType,
          fileUrl: payload.fileUrl,
          timestamp: new Date().toISOString(), // Optional: adding timestamp
        };

        // Store the entity into the Azure Table
        await tableClient.createEntity(entity);
      })
    );
  } catch (error) {
    throw new Error(`Error saving file metadata: ${error}`);
  }
}

// Function to get data from azure tables storage
export async function getFileMetadataToAzureByProudctId(prouductId: string): Promise<TableField[] | void> {
  if (!prouductId) return;

  // authenticate Table Client with SAS token
  const tableClient = await authAzureTableStorage();

  if (!tableClient) {
    throw new Error('tableClient not found');
  }

  try {
    // Query for the entity with the given partitionKey [productId]
    const entities = tableClient.listEntities<ExtendedTableEntity>({
      queryOptions: {
        filter: `PartitionKey eq '${prouductId}'`,
      },
    });

    const resultData: TableField[] = [];

    for await (const entity of entities) {
      resultData.push({
        productId: entity.partitionKey,
        filename: entity.rowKey,
        fileType: entity.fileType,
        fileUrl: entity.fileUrl,
      });
    }

    return resultData;
  } catch (error) {
    throw new Error(`Error querying file metadata: ${error}`);
  }
}

// Function to Delete data from azure tables storage
export async function deleteFileMetadataToAzureByProudctId(prouductId: string): Promise<void> {
  // authenticate Table Client with SAS token
  const tableClient = await authAzureTableStorage();

  if (!tableClient) return;

  try {
    // Retrieve all entities based on partitionKey
    const entities = tableClient.listEntities<ExtendedTableEntity>({
      queryOptions: {
        filter: `PartitionKey eq '${prouductId}'`,
      },
    });

    // Loop through the found entities and remove each entity
    for await (const entity of entities) {
      await tableClient.deleteEntity(entity.partitionKey, entity.rowKey);
    }
  } catch (error) {
    throw new Error(`Error deleting entities: ${error}`);
  }
}

export async function exportOBJFromScene(scene?: THREE.Scene, filename?: string, isDownloading = false): Promise<Blob | void> {
  if (!scene) {
    throw new Error('Scane does not empty!');
  }

  const exporter = new OBJExporter();

  // Parsing scene secara asinkron
  const result = await new Promise<string>((resolve) => {
    setTimeout(() => {
      const data = exporter.parse(scene);
      resolve(data);
    }, 0); // Memastikan proses non-blocking
  });

  const blob = new Blob([result], { type: 'text/plain' });
  const blobUrl = URL.createObjectURL(blob);

  if (!isDownloading) {
    return blob;
  }

  // Or save file .obj
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = `${filename}.obj`; // File name when downloaded
  link.target = '_blank';
  link.click();
}

export async function exportTextureFromBase64(base64: string, filename: string) {
  if (!base64) return;
  // Separate headers from base64 data
  const byteString = atob(base64.split(',')[1]);

  // Create a byte array from a binary string
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);
  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }

  // Create a Blob from a byte array
  const blob = new Blob([ab], { type: 'image/png' });

  // Create a URL for the Blob
  const blobUrl = URL.createObjectURL(blob);

  // Or save file .obj
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = filename + '.obj'; // File name when downloaded
  link.click();
}

export function convertOBJStringToMesh(objString: string): Mesh | undefined {
  try {
    const objLoader = new OBJLoader();

    // Parse string .obj ke dalam objek 3D
    const object3D = objLoader.parse(objString);

    let mesh: Mesh | undefined = undefined;
    object3D.traverse(function (child: THREE.Object3D<THREE.Object3DEventMap>) {
      if ((child as Mesh).isMesh) {
        // Pastikan THREE.MeshBasicMaterial diakses dari THREE namespace
        (child as Mesh).material = new THREE.MeshBasicMaterial({ color: 0x00ff00 });
        mesh = child as Mesh;
      }
    });

    return mesh;
  } catch (error) {
    throw new Error(`Error converting .obj string to Mesh: ${error}`);
  }
}

export function generateOBJ(meshes: THREE.Mesh[]): string {
  let objString = '';
  let vertexOffset = 0;

  meshes.forEach((mesh, meshIndex) => {
    const geometry = mesh.geometry;

    if (!geometry || !(geometry instanceof THREE.BufferGeometry)) {
      throw new Error(`Mesh ${meshIndex} does not have a valid geometry.`);
    }

    const vertices = geometry.attributes.position.array;
    const faces = geometry.index ? geometry.index.array : null;

    // Add vertices to the OBJ string
    for (let i = 0; i < vertices.length; i += 3) {
      objString += `v ${vertices[i]} ${vertices[i + 1]} ${vertices[i + 2]}\n`;
    }

    // Add faces (f) with proper vertex offset
    if (faces) {
      for (let i = 0; i < faces.length; i += 3) {
        const a = faces[i] + 1 + vertexOffset;
        const b = faces[i + 1] + 1 + vertexOffset;
        const c = faces[i + 2] + 1 + vertexOffset;
        objString += `f ${a} ${b} ${c}\n`;
      }
    }

    // Update vertexOffset for the next mesh to prevent face index overlap
    vertexOffset += vertices.length / 3;
  });

  return objString;
}

/**
 * Function to merge multiple .obj file strings into one
 * @param objFiles - Array of strings representing different .obj files
 * @returns A single merged .obj file string
 */
export function mergeObjFiles(objFiles: string[]): string {
  if (objFiles.length === 0) {
    throw new Error('Failed to merge .obj file.');
  }

  let mergedObj = '';
  let vertexOffset = 0; // To keep track of vertex index offsets

  objFiles.forEach((objFile) => {
    const lines = objFile.split('\n');

    lines.forEach((line) => {
      // Check if the line defines a vertex (v, vt, or vn) or a face (f)
      if (line.startsWith('v ') || line.startsWith('vt ') || line.startsWith('vn ')) {
        // Append vertex data directly
        mergedObj += line + '\n';
      } else if (line.startsWith('f ')) {
        // For face definitions, we need to adjust vertex indices
        const faceParts = line.split(' ').slice(1); // Split by spaces and ignore the 'f' part
        const adjustedFace = faceParts.map((facePart) => {
          // Each part of a face might refer to "vertex/texture/normal"
          const indices = facePart.split('/');
          const adjustedIndices = indices.map((index) => {
            // Adjust the vertex index by adding the current vertex offset
            return index ? (parseInt(index, 10) + vertexOffset).toString() : '';
          });
          return adjustedIndices.join('/');
        });
        mergedObj += `f ${adjustedFace.join(' ')}\n`;
      }
    });

    // After processing a file, count how many vertices (v lines) were in it
    const vertexCount = lines.filter((line) => line.startsWith('v ')).length;
    vertexOffset += vertexCount; // Update the vertex offset
  });

  return mergedObj;
}

/**
 * Function to convert a Base64 string into a Blob URL.
 * @param base64String - The base64 string representing the 3D file.
 * @param mimeType - The MIME type of the file (e.g., "model/obj" for .obj files).
 * @returns A Blob URL that can be used in the browser to reference the file.
 */
export function base64ToBlobUrl(base64String: string | null, filename: string): void {
  if (!base64String) {
    throw new Error('String is null!');
  }

  try {
    // Create a Blob from the .obj file string (use correct MIME type for .obj files)
    const blob = new Blob([base64String], { type: 'text/plain' }); // .obj files are plain text

    // Create a URL for the Blob
    const blobUrl = URL.createObjectURL(blob);

    // Anda bisa menggunakan URL ini misalnya sebagai sumber file di elemen HTML
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename + '.obj'; // Nama file ketika diunduh
    link.click();

    // Return the URL which can be used to download or reference the .obj file
    // return blobUrl;
  } catch (error) {
    throw new Error(`Error creating Blob URL: ${error}`);
  }
}

// Function to convert ArrayBuffer back to a 3D Object
export async function convertArrayBufferTo3DObject(
  arrayBuffer: ArrayBuffer,
  convertTo: Convert3DFileType // Default conversion is to Mesh
): Promise<Mesh | string | undefined> {
  try {
    // Step 1: Convert ArrayBuffer to a string
    const decoder = new TextDecoder('utf-8');
    const objString = decoder.decode(arrayBuffer);

    // If the user wants the string version of the .obj file, return it
    // Convert3DFileType 👉 (STRING or MESH)
    if (convertTo === Convert3DFileType.STRING) {
      return objString; // Return the .obj string directly
    }

    // Step 2: Use OBJLoader from Three.js to parse the string into a 3D object
    const objLoader = new OBJLoader();
    const object3D = objLoader.parse(objString);

    // Step 3: Optionally apply materials or transformations to the loaded object
    let mesh: Mesh | undefined = undefined;
    object3D.traverse(function (child: THREE.Object3D<THREE.Object3DEventMap>) {
      if ((child as Mesh).isMesh) {
        (child as Mesh).material = new MeshBasicMaterial({ color: 0x00ff00 });
        mesh = child as Mesh;
      }
    });

    return mesh; // Return the Mesh object
  } catch (error) {
    throw new Error(`Error converting ArrayBuffer to 3D object: ${error}`);
  }
}

// Function to upload 3D file .obj ...
export async function uploadFile3D(file: File, direcatoryPath: string): Promise<Product3DFile> {
  if (!file || !direcatoryPath.length) {
    throw new Error("You may not include the 3D object's File or directoryPath in the function parameter");
  }

  let fileMetadata: Product3DFile;

  // set the minimum file limit for uploading to azure blob storage
  const limitFileSize = 50 * 1024 * 1024;

  if (file.size > limitFileSize) {
    // If file > 50MB
    const fileUrl = await uploadToAzure({ file, direcatoryPath }); // Call function Upload to Azure
    fileMetadata = {
      filename: file.name,
      fileType: FileType3D.OBJ,
      storageLocation: StorageLocation.AZURE,
      fileContent: fileUrl,
    };
  } else {
    // If file <= 50MB
    // const binaryData = await convertFileToBinary(file); //Call function convert to binary data
    const blobUrl = URL.createObjectURL(file); // Create blob url
    fileMetadata = {
      filename: file.name,
      fileType: FileType3D.OBJ,
      storageLocation: StorageLocation.PSQL,
      fileContent: blobUrl,
    };
  }

  return fileMetadata;
}

// Function to convert a file to binary (ArrayBuffer) in TypeScript
export async function convertFileToBinary(file: File): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    // Event listener for when file is successfully read
    reader.onload = () => {
      // The result of FileReader is an ArrayBuffer
      if (reader.result) {
        resolve(reader.result as ArrayBuffer);
      } else {
        reject(new Error('Failed to read file'));
      }
    };

    // Event listener for errors during file reading
    reader.onerror = () => {
      reject(new Error(`Error reading file: ${reader.error?.message}`));
    };

    // Read the file as an ArrayBuffer (binary format)
    reader.readAsArrayBuffer(file);
  });
}

// Function to Chunked file (Split files into multiple small partitions to maximize uploads)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function useChunkFile(file: File, blockBlobClient: any, existingBlockIds?: string[]): Promise<string[]> {
  // Chunk size (example: 1MB per chunk)
  const chunkSize = 1 * 1024 * 1024;
  const totalChunks = Math.ceil(file.size / chunkSize);
  const blockIds: string[] = existingBlockIds?.length ? [...existingBlockIds] : [];

  // Upload each chunk separately
  for (let i = 0; i < totalChunks; i++) {
    const chunk = file.slice(i * chunkSize, (i + 1) * chunkSize); // Splitting files into chunks

    const blockId = btoa(`block-${i.toString().padStart(6, '0')}`); // Creating a unique blockId
    blockIds.push(blockId);

    // Upload chunk to Azure Blob
    await blockBlobClient.stageBlock(blockId, chunk, chunk.size);

    // Simpan progress setiap selesai upload chunk
    saveUploadProgress(file.name, blockIds);
  }

  return blockIds;
}

// Function upload file to Azure Blob Storage
export async function uploadToAzurewithChunkFile({
  file,
  direcatoryPath,
  containerName = 'uploadfile',
  type = 'general',
  existingBlockIds = [],
  fileLimit = 50,
}: {
  file: File;
  direcatoryPath: string;
  containerName?: string;
  type?: string;
  existingBlockIds?: string[];
  fileLimit?: number;
}): Promise<string> {
  const sasToken = type === 'general' ? sasTokenGeneral : sasTokenFormBuilder;
  const blobUrl = type === 'general' ? blobUrlGeneral : blobUrlFormBuilder;
  if (!blobUrl || !sasToken) {
    throw new Error('Blob URL or SAS Token is missing');
  }

  // Mulai memonitor jaringan saat proses upload dimulai
  startMonitorNetwork();

  // Modify filename metadata
  const fileName = `${direcatoryPath}/${generateFilename(file.name)}`;
  const newFile = new File([file], fileName, { type: file.type });
  const blobServiceClient = new BlobServiceClient(`${blobUrl}${sasToken}`);
  const containerClient = blobServiceClient.getContainerClient(containerName);
  const blockBlobClient = containerClient.getBlockBlobClient(newFile.name);

  const uploadOptions: BlockBlobParallelUploadOptions = {
    blobHTTPHeaders: {
      blobContentType: file.type,
    },
  };

  // Set minimum size for using chunk files (example: 3MB per chunk)
  const useChunkFileLimit = fileLimit * 1024 * 1024;

  if (file.size > useChunkFileLimit) {
    const blockIds = await useChunkFile(file, blockBlobClient, existingBlockIds);

    // save upload progress ...
    saveUploadProgress(file.name, blockIds);

    // Upload process ...
    await blockBlobClient.commitBlockList(blockIds, uploadOptions);
  } else {
    // Upload process ...
    await blockBlobClient.uploadData(file, uploadOptions);
  }

  // Setelah proses upload selesai, stop monitoring jaringan
  stopMonitoringNetwork();
  // Restart the upload from the beginning
  localStorage.removeItem('uploadProgress');

  return blockBlobClient.url;
}

// Function upload file to Azure Blob Storage with resumable if connection trouble...
export async function uploadToAzureWithResumable({
  file,
  direcatoryPath,
  // containerName = 'uploadfile',
  // type = 'general',
}: {
  file: File;
  direcatoryPath: string;
  containerName?: string;
  type?: string;
}): Promise<string> {
  // Get exiting progress ...
  const existingProgress = loadUploadProgress();
  // const existingBlockIds = existingProgress.blockIds;

  let fileUrl = '';

  // Check if there is any progress saved
  if (existingProgress && existingProgress.fileName === file.name) {
    const resume = confirm('Continue uploading from the last progress? Select “OK” to continue, or “Cancel” to start over.');
    if (resume) {
      // Continue uploading from saved progress
      const containerName = 'uploadfile';
      const type = 'general';

      fileUrl = await uploadToAzure({
        file,
        direcatoryPath,
        containerName,
        type,
        // existingBlockIds
      });
    } else {
      // Restart the upload from the beginning
      localStorage.removeItem('uploadProgress');
      fileUrl = await uploadToAzure({ file, direcatoryPath });
    }
  } else {
    // Start a new upload
    fileUrl = await uploadToAzure({ file, direcatoryPath });
  }

  return fileUrl;
}

function startMonitorNetwork() {
  // Event listener when internet connection is lost
  window.addEventListener('offline', () => alert(alertConnectionOffline));
  window.addEventListener('online', () => alert(alertConnectionOnline));
}

function stopMonitoringNetwork() {
  // Delete the event listener after the upload process is complete
  window.removeEventListener('offline', () => alert(alertConnectionOffline));
  window.removeEventListener('online', () => alert(alertConnectionOnline));
}

// Function to save upload progress in localStorage (you can customize this)
function saveUploadProgress(fileName: string, blockIds: string[]) {
  const uploadProgress = { fileName, blockIds };
  localStorage.setItem('uploadProgress', JSON.stringify(uploadProgress));
}

// Function to load upload progress from localStorage
function loadUploadProgress() {
  const progress = localStorage.getItem('uploadProgress');
  return progress ? JSON.parse(progress) : null;
}

export async function fetchEmailTemplate(filePath: string) {
  try {
    const response = await fetch(filePath);
    if (!response.ok) {
      throw new Error(`Failed to fetch template: ${response.statusText}`);
    }
    return await response.text();
  } catch (error) {
    console.error('Error fetching email template:', error);
    return '';
  }
}

export const replacePlaceholders = (template: string, data: Record<string, string | string[]>): string => {
  let updatedTemplate = template;
  let fileType = '';
  for (const key in data) {
    if (Array.isArray(data[key])) {
      if (key === 'scanUrls') fileType = 'SCAN';
      if (key === 'imageUrls') fileType = 'IMAGE';
      const placeholder = `{{${key}}}`;
      const urlsHtmls = (data[key] as string[])
        .map(
          (url: string) => `<td height="20" style="width:5%;max-width:5%;height:0;" width="5%">&nbsp;</td>
                        <td align="left" style="width:25%;max-width:25%;" width="25%">${fileType}:</td>
                        <td align="center" height="20" style="width:5%;max-width:5%;height:0;" width="10%">&nbsp;</td>
                        <td align="left" style="width:55%;max-width:55%;" width="55%"><a href="${url}" target="_blank">${url}</a></td>
                        <td height="20" style="width:5%;max-width:5%;height:0;" width="5%">&nbsp;</td>
      `
        )
        .join('');
      updatedTemplate = updatedTemplate.replace(new RegExp(placeholder, 'g'), `<tr>${urlsHtmls}</tr>`);
    } else {
      const placeholder = `{{${key}}}`;
      updatedTemplate = updatedTemplate.replace(new RegExp(placeholder, 'g'), data[key] as string);
    }
  }
  return updatedTemplate;
};

export function generateIDNumber(key = '') {
  const now = new Date();

  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hour = String(now.getHours()).padStart(2, '0');
  const minute = String(now.getMinutes()).padStart(2, '0');
  const second = String(now.getSeconds()).padStart(2, '0');

  return `${key}-${year}${month}${day}${hour}${minute}${second}`;
}
export async function extractSceneToMeshItem(objContent: string, meshNames: string[]): Promise<ExtractedMesh[]> {
  return new Promise((resolve, reject) => {
    const loader = new OBJLoader();
    const exporter = new OBJExporter();

    try {
      // Parse langsung dari string konten .obj
      const group = loader.parse(objContent);

      const result: ExtractedMesh[] = [];

      group.children.forEach((child: THREE.Object3D<THREE.Object3DEventMap>, index: number) => {
        if (child instanceof Mesh) {
          const objContent = exporter.parse(child) as string;
          const base64Content = btoa(unescape(encodeURIComponent(objContent)));
          const name = meshNames[index] || `mesh_${index}`;
          result.push({ name, base64Content });
        }
      });

      resolve(result);
    } catch (error) {
      reject(error);
    }
  });
}

export async function downloadFile(file: Blob, filename: string): Promise<void> {
  const blobUrl = URL.createObjectURL(file);
  // Download file
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = filename; // Nama file ketika diunduh
  link.click();
}

export function generateThumbnail(file: File, maxSize = 100): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    const reader = new FileReader()

    reader.onload = (e) => {
      img.onload = () => {
        // Hitung ukuran thumbnail
        const ratio = Math.min(maxSize / img.width, maxSize / img.height)
        const width = img.width * ratio
        const height = img.height * ratio

        const canvas = document.createElement('canvas')
        canvas.width = width
        canvas.height = height

        const ctx = canvas.getContext('2d')
        if (!ctx) return reject('Canvas not supported')

        ctx.drawImage(img, 0, 0, width, height)

        const thumbnailDataUrl = canvas.toDataURL('image/jpeg', 0.8) // bisa juga image/png
        resolve(thumbnailDataUrl)
      }

      img.onerror = (err) => reject(err)
      img.src = e.target?.result as string
    }

    reader.onerror = (err) => reject(err)
    reader.readAsDataURL(file)
  })
}

export function base64ToFile(
  base64: string,
  filename: string,
  mimeType = ''
): File {
  const [header, data] = base64.split(',');
  const mime = mimeType || (() => {
    const colon = header.indexOf(':');
    if (colon === -1) return '';
    const semicolon = header.indexOf(';', colon);
    return semicolon === -1 ? '' : header.slice(colon + 1, semicolon);
  })();

  const binary = atob(data);
  const u8arr = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    u8arr[i] = binary.charCodeAt(i);
  }

  return new File([u8arr], filename, { type: mime });
}



